/**
 * Centralized Step Styles and Icons
 * Ensures consistency between Grid and Flow modes
 * MUST match StepType in ../types.ts
 * 
 * NOTE: Use 'offer' for all offer types (no separate upsell/downsell)
 */

import { Home, ShoppingCart, Gift, CheckCircle, Box, AlertTriangle, ExternalLink, Store, ShoppingBag } from 'lucide-react';
import { StepType } from '../types';

/**
 * Step type icons mapping
 * Used in both Grid mode (StepBoxWithVariants) and Flow mode (FlowStepNode)
 * MUST match all StepType values
 */
export const STEP_ICONS: Record<StepType, any> = {
  landing: Home,
  checkout: ShoppingCart,
  offer: Gift,
  thankyou: CheckCircle,
  custom: Box,
  error: AlertTriangle,
  external: ExternalLink,
  storefront_shopify: Store,
  storefront_woocommerce: ShoppingBag,
};

/**
 * Step type background colors
 * Used for icon backgrounds and visual identification
 */
export const STEP_BG_COLORS: Record<StepType, string> = {
  landing: 'bg-blue-500',
  checkout: 'bg-green-500',
  offer: 'bg-purple-500',
  thankyou: 'bg-pink-500',
  custom: 'bg-slate-500',
  error: 'bg-red-500',
  external: 'bg-orange-500',
  storefront_shopify: 'bg-emerald-600',
  storefront_woocommerce: 'bg-violet-600',
};

/**
 * Step type text colors
 * For badges and labels
 */
export const STEP_TEXT_COLORS: Record<StepType, string> = {
  landing: 'text-blue-700',
  checkout: 'text-green-700',
  offer: 'text-purple-700',
  thankyou: 'text-pink-700',
  custom: 'text-slate-700',
  error: 'text-red-700',
  external: 'text-orange-700',
  storefront_shopify: 'text-emerald-700',
  storefront_woocommerce: 'text-violet-700',
};

/**
 * Step type border colors
 * For highlighting and selection states
 */
export const STEP_BORDER_COLORS: Record<StepType, string> = {
  landing: 'border-blue-500',
  checkout: 'border-green-500',
  offer: 'border-purple-500',
  thankyou: 'border-pink-500',
  custom: 'border-slate-500',
  error: 'border-red-500',
  external: 'border-orange-500',
  storefront_shopify: 'border-emerald-600',
  storefront_woocommerce: 'border-violet-600',
};

/**
 * Step type ring colors
 * For focus and hover states
 */
export const STEP_RING_COLORS: Record<StepType, string> = {
  landing: 'ring-blue-200',
  checkout: 'ring-green-200',
  offer: 'ring-purple-200',
  thankyou: 'ring-pink-200',
  custom: 'ring-slate-200',
  error: 'ring-red-200',
  external: 'ring-orange-200',
  storefront_shopify: 'ring-emerald-200',
  storefront_woocommerce: 'ring-violet-200',
};

/**
 * Get icon component for a step type
 */
export function getStepIcon(type: StepType) {
  return STEP_ICONS[type] || Box;
}

/**
 * Get background color class for a step type
 */
export function getStepBgColor(type: StepType): string {
  return STEP_BG_COLORS[type] || 'bg-slate-500';
}

/**
 * Get text color class for a step type
 */
export function getStepTextColor(type: StepType): string {
  return STEP_TEXT_COLORS[type] || 'text-slate-700';
}

/**
 * Get border color class for a step type
 */
export function getStepBorderColor(type: StepType): string {
  return STEP_BORDER_COLORS[type] || 'border-slate-500';
}

/**
 * Get ring color class for a step type
 */
export function getStepRingColor(type: StepType): string {
  return STEP_RING_COLORS[type] || 'ring-slate-200';
}

/**
 * Step type display names
 */
export const STEP_TYPE_LABELS: Record<StepType, string> = {
  landing: 'Landing',
  checkout: 'Checkout',
  offer: 'Offer',
  thankyou: 'Thank You',
  custom: 'Custom',
  error: 'Error',
  external: 'External Page',
  storefront_shopify: 'Shopify',
  storefront_woocommerce: 'WooCommerce',
};

/**
 * Get display label for a step type
 */
export function getStepTypeLabel(type: StepType): string {
  return STEP_TYPE_LABELS[type] || type;
}
